import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/auth/useAuth';
import { fetchWorkflowDraftStatuses } from '@/services/workflowDraftStatusService';
import type { Workflow } from '@/types/logicApps';

export const workflowDraftStatusesQueryPrefix = (
  projectName?: string,
  appName?: string,
) => [
  'workflowDraftStatuses',
  projectName?.toLowerCase(),
  appName?.toLowerCase(),
] as const;

export const workflowDraftStatusesQueryKey = (
  projectName: string,
  appName: string,
  workflows: readonly Workflow[],
) => [
  ...workflowDraftStatusesQueryPrefix(projectName, appName),
  workflows
    .map((workflow) => [workflow.id, workflow.properties.changedTime ?? null] as const)
    .sort(([left], [right]) => left.localeCompare(right)),
] as const;

export const useWorkflowDraftStatuses = (
  workflows: readonly Workflow[],
  projectName: string | undefined,
  appName: string | undefined,
  enabled: boolean,
) => {
  const { getOttoToken } = useAuth();
  return useQuery({
    queryKey: workflowDraftStatusesQueryKey(
      projectName ?? '',
      appName ?? '',
      workflows,
    ),
    queryFn: ({ signal }) =>
      fetchWorkflowDraftStatuses(
        {
          projectName: projectName!,
          appName: appName!,
          getToken: getOttoToken,
        },
        workflows,
        undefined,
        signal,
      ),
    enabled: enabled && !!projectName && !!appName && workflows.length > 0,
    staleTime: 0,
    refetchOnMount: 'always',
  });
};
